import { useState, useEffect, ReactNode } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Filter, Download, Trash2, Clock, Eye, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { memoryExplorerApi, MemoryExplorerDto, MemoryExplorerRequest, GraphSearchResult, TopicsDto } from '@/services/memoryExplorerApi';
import { toast } from "@/components/ui/sonner";
import cytoscape, { Core as CytoscapeCore } from 'cytoscape';
import { useRef, useCallback } from 'react';
import { formatLocalDate, formatLocalDateTime } from '@/lib/date';

// Interface for Memory Entry data structure used in this component
interface MemoryEntry {
  id: string;
  user_id: string;
  team_id: string;
  project_id: string;
  context: string;
  tags: string[];
  categories: string[];
  metadata?: Record<string, unknown>;
  ttl_days?: number;
  expires_at?: string;
  last_recalled?: string;
  consent: boolean;
  created_at: string;
}

// Utility: sort strings case-insensitively without mutating original array
const sortStringsCaseInsensitive = (values: string[]): string[] => {
  return [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};

const MemoryEntries = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTag, setFilterTag] = useState('all');

  const [filterEnvironment, setFilterEnvironment] = useState('production');
  const [filterProject, setFilterProject] = useState('all');
  const [filterMemoryType, setFilterMemoryType] = useState('facts');
  const [availableProjects, setAvailableProjects] = useState<string[]>([]);
  const [memoryEntries, setMemoryEntries] = useState<MemoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 10;
  const cyRef = useRef<CytoscapeCore | null>(null);
  const [graphData, setGraphData] = useState<GraphSearchResult[]>([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState<string | null>(null);
  const [selectedGraphUserId, setSelectedGraphUserId] = useState<string>('');
  const [graphUserIds, setGraphUserIds] = useState<string[]>([]);
  // Attributes state
  const [attributesData, setAttributesData] = useState<TopicsDto | null>(null);
  const [attributesLoading, setAttributesLoading] = useState(false);
  const [attributesError, setAttributesError] = useState<string | null>(null);
  const [selectedAttributesUserId, setSelectedAttributesUserId] = useState<string>('all');
  const [attributesUserIds, setAttributesUserIds] = useState<string[]>([]);
  const [allUsersTopics, setAllUsersTopics] = useState<Record<string, TopicsDto['topics']>>({});

  // Convert MemoryExplorerDto to MemoryEntry
  const convertToMemoryEntry = (dto: MemoryExplorerDto): MemoryEntry => {
    // Calculate TTL days from expires_at if available
    let ttl_days = undefined;
    let expires_at = undefined;
    
    if (dto.ttl) {
      // Parse TTL string (e.g., "30d", "24h", "1w")
      const ttlMatch = dto.ttl.match(/(\d+)([hdw])/);
      if (ttlMatch) {
        const value = parseInt(ttlMatch[1]);
        const unit = ttlMatch[2];
        switch (unit) {
          case 'h':
            ttl_days = Math.ceil(value / 24);
            break;
          case 'd':
            ttl_days = value;
            break;
          case 'w':
            ttl_days = value * 7;
            break;
        }
        
        // Calculate expires_at based on created_at and TTL
        const createdDate = new Date(dto.timestamp);
        const expiresDate = new Date(createdDate);
        expiresDate.setDate(expiresDate.getDate() + ttl_days);
        expires_at = formatLocalDate(expiresDate);
      }
    }

    return {
      id: dto.id,
      user_id: dto.scope.type === 'user' ? dto.scope.id : 'system',
      team_id: 'team_' + dto.id.substring(0, 8), // Derived from memory ID
      project_id: dto.projectId || 'default', // Use project_id from DTO
      context: dto.snippet,
      tags: dto.tags || [], // Use tags from DTO
      categories: dto.categories || [],
      metadata: dto.metadata,
      ttl_days,
      expires_at,
      last_recalled: undefined, // This would need to be tracked separately in the backend
      consent: true, // Assuming consent is required to store memories
      created_at: dto.timestamp,
    };
  };

  // Get unique tags from all entries
  const allTags = [...new Set(memoryEntries.flatMap(entry => entry.tags))];

  // Fetch available projects
  const fetchAvailableProjects = useCallback(async () => {
    try {
      const projects = await memoryExplorerApi.getAvailableProjects();
      setAvailableProjects(projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to fetch available projects');
    }
  }, []);

  // Fetch memories from API
  const fetchMemories = useCallback(async () => {
    setLoading(true);
    try {
      const request: MemoryExplorerRequest = {
        project: filterProject === 'all' ? 'all' : filterProject,
        search: searchTerm,
        scopes: [],
        environment: filterEnvironment,
        dateFrom: '',
        dateTo: '',
        sortBy: 'timestamp',
        sortDir: 'desc',
        ttl: 'all',
        page: currentPage,
        pageSize: itemsPerPage,
        includeDeleted: false,
        memoryType: filterMemoryType,
      };

      const response = await memoryExplorerApi.getMemories(request);
      
      // Convert DTOs to MemoryEntry format
      const entries = response.memories.map(convertToMemoryEntry);
      
      setMemoryEntries(entries);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Error fetching memory entries:', error);
      toast.error('Failed to fetch memory entries', {
        description: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setLoading(false);
    }
  }, [filterProject, searchTerm, filterEnvironment, currentPage, itemsPerPage, filterMemoryType]);

  // Fetch data when filters change
  useEffect(() => {
    fetchMemories();
  }, [fetchMemories, searchTerm, filterTag, filterEnvironment, filterProject, filterMemoryType, currentPage]);

  // Fetch available projects on component mount
  useEffect(() => {
    fetchAvailableProjects();
  }, [fetchAvailableProjects]);

  // Handle search input with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Handle filter changes
  const handleTagFilterChange = (value: string) => {
    setFilterTag(value);
    setCurrentPage(1);
  };



  const handleProjectFilterChange = (value: string) => {
    setFilterProject(value);
    setCurrentPage(1);
  };

  const renderMetadata = (metadata: unknown): ReactNode => {
    if (metadata == null) {
      return <span className="text-muted-foreground">—</span>;
    }

    let normalized: unknown = metadata;
    if (typeof normalized === 'string') {
      try {
        normalized = JSON.parse(normalized);
      } catch {
        return <span className="break-words">{String(normalized)}</span>;
      }
    }

    if (Array.isArray(normalized)) {
      if (normalized.length === 0) return <span className="text-muted-foreground">—</span>;
      return (
        <div className="space-y-1">
          {normalized.map((item, idx) => (
            <div key={idx} className="break-words">
              <span className="text-muted-foreground">{idx}:</span> {typeof item === 'string' ? item : JSON.stringify(item)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof normalized === 'object') {
      const entries = Object.entries(normalized as Record<string, unknown>);
      if (entries.length === 0) return <span className="text-muted-foreground">—</span>;
      return (
        <div className="space-y-1">
          {entries.map(([key, value]) => (
            <div key={key} className="break-words">
              <span className="text-muted-foreground">{key}:</span> {typeof value === 'string' ? value : JSON.stringify(value)}
            </div>
          ))}
        </div>
      );
    }

    return <span className="break-words">{String(normalized)}</span>;
  };

  const handleDeleteMemory = async (memoryId: string) => {
    try {
      await memoryExplorerApi.softDeleteMemory(memoryId);
      toast.success('Memory moved to trash');
      fetchMemories(); // Refresh the list
    } catch (error) {
      toast.error('Failed to delete memory');
    }
  };

  const handleExportCSV = async () => {
    try {
      // Prepare CSV data
      const csvHeaders = [
        'ID',
        'User ID',
        'Project ID',
        'Memory',
        'Tags',
        'Categories',
        'Metadata',
        'TTL Days',
        'Expires At',
        'Created At'
      ];

      const csvData = filteredEntries.map(entry => [
        entry.id,
        entry.user_id,
        entry.project_id,
        entry.context,
        entry.tags.join(', '),
        (entry.categories || []).join(', '),
        JSON.stringify(entry.metadata ?? {}),
        entry.ttl_days || '',
        entry.expires_at || '',
        entry.created_at
      ]);

      // Create CSV content
      const csvContent = [
        csvHeaders.join(','),
        ...csvData.map(row => 
          row.map(cell => 
            // Escape quotes and wrap in quotes if contains comma, newline, or quote
            typeof cell === 'string' && (cell.includes(',') || cell.includes('\n') || cell.includes('"'))
              ? `"${cell.replace(/"/g, '""')}"`
              : cell
          ).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `memory-entries-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  // Filter entries based on current filters (for client-side filtering)
  const filteredEntries = memoryEntries.filter(entry => {
    const matchesSearch = entry.context.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.user_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = filterTag === 'all' || entry.tags.includes(filterTag);
    
    return matchesSearch && matchesTag;
  });

  // Fetch userIds when entering Graph Memory mode
  useEffect(() => {
    if (filterMemoryType === 'graph') {
      setGraphUserIds([]);
      setSelectedGraphUserId('');
      memoryExplorerApi.getGraphUserIds()
        .then(userIds => {
          setGraphUserIds(userIds);
          if (userIds.length > 0) {
            setSelectedGraphUserId(userIds[0]);
          }
        })
        .catch(() => {
          setGraphUserIds([]);
        });
    }
  }, [filterMemoryType]);

  // Fetch users + aggregated topics when entering Attributes or project changes
  useEffect(() => {
    if (filterMemoryType !== 'attributes') return;
    const projectId = filterProject !== 'all' ? filterProject : undefined;
    setAttributesLoading(true);
    setAttributesError(null);
    // Call without userId to get aggregated topics and the user list
    memoryExplorerApi.getExplorerTopics(undefined, projectId)
      .then((data: TopicsDto) => {
        setAttributesData(data);
        setAttributesUserIds(sortStringsCaseInsensitive(data.users || []));
        setSelectedAttributesUserId('all'); // default to All Users sentinel
        setAttributesLoading(false);
      })
      .catch(err => {
        setAttributesError(err.message);
        setAttributesLoading(false);
      });
  }, [filterMemoryType, filterProject]);

  // Fetch topics data when Attributes selected and user changes (specific user selection)
  useEffect(() => {
    if (filterMemoryType !== 'attributes') return;
    const projectId = filterProject !== 'all' ? filterProject : undefined;
    if (selectedAttributesUserId === 'all') {
      // All users: re-fetch aggregated topics (without userId)
      setAttributesLoading(true);
      setAttributesError(null);
      memoryExplorerApi.getExplorerTopics(undefined, projectId)
        .then((data: TopicsDto) => {
          setAttributesData(data);
          setAttributesLoading(false);
        })
        .catch(err => {
          setAttributesError(err.message);
          setAttributesLoading(false);
        });
      return;
    }
    setAttributesLoading(true);
    setAttributesError(null);
    memoryExplorerApi.getExplorerTopics(selectedAttributesUserId, projectId)
      .then((data: TopicsDto) => {
        setAttributesData(data);
        setAttributesLoading(false);
      })
      .catch(err => {
        setAttributesError(err.message);
        setAttributesLoading(false);
      });
  }, [filterMemoryType, selectedAttributesUserId, filterProject]);

  // When 'All Users' is selected, fetch topics for each user and group them
  useEffect(() => {
    if (filterMemoryType !== 'attributes' || selectedAttributesUserId !== 'all' || attributesUserIds.length === 0) return;
    const projectId = filterProject !== 'all' ? filterProject : undefined;
    setAttributesLoading(true);
    Promise.all(
      attributesUserIds.map(uid =>
        memoryExplorerApi.getExplorerTopics(uid, projectId)
          .then((data: TopicsDto) => [uid, data.topics] as [string, TopicsDto['topics']])
          .catch(() => [uid, []] as [string, TopicsDto['topics']])
      )
    )
      .then(pairs => {
        const grouped: Record<string, TopicsDto['topics']> = {};
        pairs.forEach(([uid, topics]) => { grouped[uid] = topics; });
        setAllUsersTopics(grouped);
        setAttributesLoading(false);
      })
      .catch(() => {
        setAttributesLoading(false);
      });
  }, [filterMemoryType, selectedAttributesUserId, attributesUserIds, filterProject]);

  // Fetch graph relationships when 'Graph Memory' is selected and userId changes
  useEffect(() => {
    if (filterMemoryType !== 'graph' || !selectedGraphUserId) return;
    setGraphLoading(true);
    setGraphError(null);
    const projectId = filterProject !== 'all' ? filterProject : undefined;
    memoryExplorerApi.getGraphRelationships(selectedGraphUserId, projectId)
      .then((data: GraphSearchResult[]) => {
        setGraphData(data);
        setGraphLoading(false);
      })
      .catch(err => {
        setGraphError(err.message);
        setGraphLoading(false);
      });
  }, [filterMemoryType, selectedGraphUserId, filterProject]);

  // Render Cytoscape graph when graphData changes
  useEffect(() => {
    if (filterMemoryType !== 'graph' || !graphData || graphLoading || graphError) return;
    const elements = graphData.map((r) => ({
      data: { id: `${r.source}-${r.destination}`, source: r.source, target: r.destination, label: r.relationship }
    }));
    const nodes: Record<string, boolean> = {};
    graphData.forEach((r) => {
      nodes[r.source] = true;
      nodes[r.destination] = true;
    });
    const nodeElements = Object.keys(nodes).map(id => ({ data: { id } }));
    const allElements = [...nodeElements, ...elements];
    if (cyRef.current) {
      cyRef.current.destroy();
      cyRef.current = null;
    }
    cyRef.current = cytoscape({
      container: document.getElementById('graph-memory-cytoscape'),
      elements: allElements,
      style: [
        {
          selector: 'node',
          style: {
            label: 'data(id)',
            'background-color': '#9CFFB6', // recallio green
            color: '#111', // dark text for contrast
            'text-valign': 'center',
            'text-halign': 'center',
            'text-outline-width': 1,
            'text-outline-color': '#9CFFB6',
            'font-size': '11px',
          }
        },
        {
          selector: 'edge',
          style: {
            label: 'data(label)',
            color: '#fff', // white relationship text
            'curve-style': 'bezier',
            'target-arrow-shape': 'triangle',
            'target-arrow-color': '#FFF79C', // recallio yellow
            'line-color': '#FFF79C', // recallio yellow
            'font-size': '11px',
            'text-background-color': '#222',
            'text-background-opacity': 0.7,
            'text-background-padding': '2px',
          }
        }
      ],
      layout: { name: 'cose' }
    });
  }, [graphData, filterMemoryType, graphLoading, graphError]);

  return (
    <PageLayout 
      title="Memory Entries" 
      description="Browse, filter, and manage all stored memory entries"
    >
      <div className="space-y-6">
        {/* Filters */}
        <Card className="p-6">
          <div className="flex flex-wrap gap-4">
            {!(filterMemoryType === 'graph' || filterMemoryType === 'attributes') && (
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by memory or user ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            )}
            
            <Select value={filterMemoryType} onValueChange={setFilterMemoryType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Memory Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="facts">Facts</SelectItem>
                <SelectItem value="raw">Raw Memory</SelectItem>
                <SelectItem value="summary">Summary</SelectItem>
                <SelectItem value="graph">Graph Memory</SelectItem>
                <SelectItem value="attributes">Attributes</SelectItem>
              </SelectContent>
            </Select>
            {!(filterMemoryType === 'graph' || filterMemoryType === 'attributes') && (
              <Select value={filterEnvironment} onValueChange={setFilterEnvironment}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Environment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="staging">Staging</SelectItem>
                  <SelectItem value="development">Development</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={filterProject} onValueChange={handleProjectFilterChange}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {(filterMemoryType === 'attributes' ? sortStringsCaseInsensitive(availableProjects) : availableProjects).map(project => (
                  <SelectItem key={project} value={project}>{project}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!(filterMemoryType === 'graph' || filterMemoryType === 'attributes') && (
              <Select value={filterTag} onValueChange={handleTagFilterChange}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter by tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {allTags.map(tag => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {filterMemoryType === 'graph' && (
              <Select value={selectedGraphUserId} onValueChange={setSelectedGraphUserId}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select User" />
                </SelectTrigger>
                <SelectContent>
                  {graphUserIds.map(userId => (
                    <SelectItem key={userId} value={userId}>{userId}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {filterMemoryType === 'attributes' && (
              <Select value={selectedAttributesUserId} onValueChange={setSelectedAttributesUserId}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {attributesUserIds.map(userId => (
                    <SelectItem key={userId} value={userId}>{userId}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {!(filterMemoryType === 'graph' || filterMemoryType === 'attributes') && (
              <Button variant="outline" onClick={handleExportCSV} className="flex items-center gap-2">
                <Download size={16} />
                Export CSV
              </Button>
            )}
          </div>
        </Card>

        {/* Results count */}
        {!(filterMemoryType === 'graph' || filterMemoryType === 'attributes') && (
          <div className="text-sm text-muted-foreground">
            {loading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading memory entries...
              </div>
            ) : (
              `Showing ${filteredEntries.length} of ${totalCount} entries`
            )}
          </div>
        )}

        {/* Memory Entries Table, Graph, or Attributes */}
        {filterMemoryType === 'graph' ? (
          <Card className="p-6">
            {graphLoading ? (
              <div style={{ position: 'relative', width: '100%', height: 500 }}>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div className="flex flex-col items-center justify-center gap-4">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                    <span className="text-lg font-semibold">Loading graph...</span>
                  </div>
                </div>
              </div>
            ) : graphError ? (
              <div className="text-red-500">{graphError}</div>
            ) : (
              <div id="graph-memory-cytoscape" style={{ width: '100%', height: 500 }} />
            )}
          </Card>
        ) : filterMemoryType === 'attributes' ? (
          <Card className="p-6">
            {attributesLoading ? (
              <div className="flex items-center justify-center gap-2 py-16">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading attributes...
              </div>
            ) : attributesError ? (
              <div className="text-red-500">{attributesError}</div>
            ) : selectedAttributesUserId === 'all' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {attributesUserIds.map(uid => (
                  <div key={uid} className="border rounded-md">
                    <div className="px-4 py-2 font-semibold border-b bg-muted/50">{uid}</div>
                    <div className="p-4">
                      {(allUsersTopics[uid] && allUsersTopics[uid].length > 0) ? (
                        <div className="grid grid-cols-1 gap-4">
                          {allUsersTopics[uid].map(topic => (
                            <div key={topic.name} className="border rounded-md">
                              <div className="px-3 py-1.5 font-medium border-b bg-muted/40">{topic.name}</div>
                              <div className="p-3">
                                <Table>
                                  <TableBody>
                                    {topic.subTopics.map((st, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="font-medium">{st.name}</TableCell>
                                        <TableCell>{st.value}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">No attributes</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : attributesData && attributesData.topics.length > 0 ? (
              <>
                <div className="mb-4 text-sm">
                  <span className="text-muted-foreground">User:</span>{' '}
                  <span className="font-semibold">{selectedAttributesUserId}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {attributesData.topics.map((topic) => (
                    <div key={topic.name} className="border rounded-md">
                      <div className="px-4 py-2 font-semibold border-b bg-muted/50">{topic.name}</div>
                      <div className="p-4">
                        <Table>
                          <TableBody>
                            {topic.subTopics.map((st, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">{st.name}</TableCell>
                                <TableCell>{st.value}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">No attributes found</div>
            )}
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User/Project</TableHead>
                  <TableHead>
                    Memory Content {filterMemoryType === 'facts' ? '(Facts)' : 
                      filterMemoryType === 'raw' ? '(Raw Memory)' : 
                      filterMemoryType === 'summary' ? '(Summary)' : ''}
                  </TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Categories</TableHead>
                  <TableHead>Metadata</TableHead>
                  <TableHead>TTL/Expires</TableHead>
                  <TableHead>Created At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading memory entries...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                      No memory entries found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-mono text-sm">{entry.user_id}</div>
                          <div className="text-xs text-muted-foreground">{entry.project_id}</div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[300px]">
                        <div className="whitespace-pre-wrap break-words">{entry.context}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {entry.tags.map(tag => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {entry.categories && entry.categories.map(category => (
                            <Badge key={category} variant="secondary" className="text-xs">
                              {category}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[300px] text-xs bg-muted p-2 rounded">
                          {renderMetadata(entry.metadata)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {entry.ttl_days ? (
                            <>
                              <div className="text-sm">{entry.ttl_days}d TTL</div>
                              <div className="text-xs text-muted-foreground">{entry.expires_at}</div>
                            </>
                          ) : (
                            <div className="text-sm text-muted-foreground">No expiry</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{formatLocalDateTime(entry.created_at)}</div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Pagination */}
        {!(filterMemoryType === 'graph' || filterMemoryType === 'attributes') && !loading && totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default MemoryEntries;
